"use client";

import { useState, useEffect, useTransition } from "react";
import {
  Stack, Group, Button, Alert, Text, NumberInput, SegmentedControl, Switch,
  Divider, TextInput,
} from "@forestsync/ui"
import { useTranslations } from "next-intl";
import { TbAlertTriangle } from "react-icons/tb";
import { submitTradeOrder } from "@/actions/trading"
import { calculateSecondaryCommission } from "@forestsync/lib/commission"
import { TRADE_ORDER_SIDE, TRADE_ORDER_TYPE, TRADE_FILL_TYPE, TRADE_ORDER_TIME_IN_FORCE } from "@forestsync/db"
import { nextSessionOpen, isMarketOpen } from "@forestsync/lib/formatting"
import { useAnalytics } from "@/components/analytics/AnalyticsProvider";
import { TradingProfileModal } from "./TradingProfileModal"
import { OrderConfirmModal } from "./OrderConfirmModal"

import type { TradeFill } from "./TradeBlotter";

interface OrderTicketProps {
  creditType: string
  vintage: number
  projectRefId: string
  creditClass?: 1 | 2
  onOrderFilled?: (fill: TradeFill) => void
  availableBalance?: number | null
  onFundAccount?: () => void
  bestAsk?: number | null
  bestBid?: number | null
}

export function OrderTicket({ creditType, vintage, projectRefId, creditClass = 2, onOrderFilled, availableBalance, onFundAccount, bestAsk, bestBid }: OrderTicketProps) {
  const t = useTranslations("trading");
  const { trackEvent } = useAnalytics();
  const [isPending] = useTransition();

  const [side, setSide] = useState<"1" | "2">("1"); // 1=BID, 2=ASK
  const [orderType, setOrderType] = useState<"1" | "2" | "3">("1"); // 1=LIMIT, 2=MARKET, 3=STOP
  const [price, setPrice] = useState<number | string>("");
  const [stopPrice, setStopPrice] = useState<number | string>("");
  const [volume, setVolume] = useState<number | string>("");
  const [allOrNothing, setAllOrNothing] = useState(false);
  const [timeInForce, setTimeInForce] = useState<"DAY" | "GTC" | "CUSTOM">("GTC");
  const [customExpiry, setCustomExpiry] = useState<string>("")
  const [error, setError] = useState<string | null>(null)
  const [profileModalOpen, setProfileModalOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingOrderInput, setPendingOrderInput] = useState<Parameters<typeof submitTradeOrder>[0] | null>(null)
  const [breakdown, setBreakdown] = useState<{ subtotal: number; platformFee: number; totalDue: number } | null>(null)
  const [marketOpen, setMarketOpen] = useState(() => isMarketOpen())
  useEffect(() => {
    const id = setInterval(() => setMarketOpen(isMarketOpen()), 1_000)
    return () => clearInterval(id)
  }, [])
  // Auto-switch away from MARKET if session closes while the ticket is open
  useEffect(() => {
    if (!marketOpen && orderType === "2") setOrderType("1")
  }, [marketOpen, orderType])

  const isMarket = orderType === "2"
  const isStop = orderType === "3"
  const isLimitOrStop = !isMarket

  const dayOrderExpiresAt = isLimitOrStop && timeInForce === "DAY" ? nextSessionOpen() : null
  const customExpiryDate = timeInForce === "CUSTOM" && customExpiry ? new Date(customExpiry) : null

  const qty = Number(volume)
  const prc = Number(price)
  // For market orders use the live best ask (BID side) or best bid (ASK side) as the estimated price
  const marketPrice = side === "1" ? (bestAsk ?? null) : (bestBid ?? null)
  const effectivePrice = isMarket ? (marketPrice ?? 0) : prc
  const subtotal = qty > 0 && effectivePrice > 0 ? qty * effectivePrice : 0
  const feeResult =
    subtotal > 0
      ? calculateSecondaryCommission({
          quantity: qty,
          pricePerTon: effectivePrice,
          platformAnnualVolume: 0,
        })
      : null
  const platformFee = feeResult?.commission ?? 0
  const totalDue = subtotal + platformFee;
    // For traders: check if their available balance covers the total due (BID only)
    const insufficientBalance = side === "1" && availableBalance !== null && availableBalance !== undefined && totalDue > availableBalance;

  // Advisory only — a crossed limit order is a legitimate CLOB order type and must still be
  // submittable. SELL below the best bid/ask undersells the market; BUY above it overpays.
  const referencePrice = side === "1" ? bestAsk : bestBid
  const crossedPriceWarning =
    !isMarket && !isStop && prc > 0 && referencePrice != null && referencePrice > 0
      ? side === "2" && prc < referencePrice
        ? t("sellBelowMarket", { price: referencePrice.toFixed(2) })
        : side === "1" && prc > referencePrice
          ? t("buyAboveMarket", { price: referencePrice.toFixed(2) })
          : null
      : null
  function handleSubmit() {
    setError(null)

    const vol = Number(volume);
    const prc = Number(price);

    if (!vol || vol <= 0) { setError(t("volumeError")); return; }
    if (!isMarket && (!prc || prc <= 0)) { setError(t("limitPriceError")); return; }
    if (isStop && (!Number(stopPrice) || Number(stopPrice) <= 0)) { setError(t("stopPriceError")); return; }
    if (timeInForce === "CUSTOM" && (!customExpiry || !customExpiryDate || customExpiryDate <= new Date())) {
      setError(t("customExpiryRequired")); return;
    }

    const orderInput = {
      creditType,
      vintage,
      projectRefId,
      creditClass,
      side: Number(side) as 1 | 2,
      orderType: Number(orderType) as 1 | 2 | 3,
      price: isMarket ? undefined : prc,
      stopPrice: isStop ? Number(stopPrice) : undefined,
      volume: vol,
      fillType: allOrNothing ? TRADE_FILL_TYPE.ALL_OR_NOTHING : TRADE_FILL_TYPE.PARTIAL,
      orderTimeInForce: isMarket ? TRADE_ORDER_TIME_IN_FORCE.GTC : timeInForce === "CUSTOM" ? TRADE_ORDER_TIME_IN_FORCE.GTC : timeInForce,
      expiresAt: customExpiryDate ?? undefined,
    }
    
    const submitPrice = isMarket ? (marketPrice ?? 0) : prc
    const subtotal = submitPrice * vol
    const submitFee = calculateSecondaryCommission({ quantity: vol, pricePerTon: submitPrice, platformAnnualVolume: 0 })
    const platformFee = submitFee?.commission ?? 0
    const totalDue = subtotal + platformFee;
    
    const bd = { subtotal, platformFee, totalDue }

    setPendingOrderInput(orderInput)
    setBreakdown(bd)
    setConfirmOpen(true)
  }

  function handleOrderSuccess(fill: TradeFill | null) {
    setVolume("")
    setPrice("")
    setConfirmOpen(false)
    setPendingOrderInput(null)
    setBreakdown(null)
    if (fill && onOrderFilled) {
      onOrderFilled(fill)
      trackEvent({
        name: "order_placed",
        mode: "trader",
        side: fill.side === TRADE_ORDER_SIDE.BID ? "bid" : "ask",
        order_type: isMarket ? "market" : isStop ? "stop" : "limit",
        credit_type: creditType,
        volume: fill.volume,
      })
    }
  }

  return (
    <>
      <Stack gap="xs" p="xs">
      {/* Side selector */}
      <SegmentedControl
        fullWidth
        value={side}
        onChange={(v) => setSide(v as "1" | "2")}
        data={[
          { label: t("buy"), value: "1" },
          { label: t("sell"), value: "2" },
        ]}
        color={side === "1" ? "green" : "red"}
      />

          {/* Order type — Stop is hidden: nothing triggers a resting STOP order today, so it's
              not offered as an option rather than shown broken. See order.ts submitTradeOrder,
              which also rejects orderType=STOP server-side as a backstop. */}
          <SegmentedControl
            fullWidth
            value={orderType}
            onChange={(v) => setOrderType(v as "1" | "2" | "3")}
            data={[
              { label: t("limit"), value: "1" },
              { label: t("market"), value: "2", disabled: !marketOpen },
            ]}
            size="xs"
            title={!marketOpen ? t("marketOrdersSessionOnly") : undefined}
          />

          {/* Time-in-force: only shown for LIMIT/STOP orders */}
          {isLimitOrStop && (
            <>
              <SegmentedControl
                fullWidth
                value={timeInForce}
                onChange={(v) => setTimeInForce(v as "DAY" | "GTC" | "CUSTOM")}
                data={[
                  { label: t("day"), value: "DAY" },
                  { label: t("gtc"), value: "GTC" },
                  { label: t("custom"), value: "CUSTOM" },
                ]}
                size="xs"
              />
              {timeInForce === "CUSTOM" && (
                <TextInput
                  type="datetime-local"
                  label={t("customExpiryLabel")}
                  value={customExpiry}
                  onChange={(e) => setCustomExpiry(e.currentTarget.value)}
                  min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
                  size="sm"
                />
              )}
              {dayOrderExpiresAt && timeInForce === "DAY" && (
                <Text size="xs" c="dimmed">
                  {t("dayOrderExpiry", {
                    date: dayOrderExpiresAt.toLocaleString(undefined, {
                      timeZone: "Europe/Oslo",
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZoneName: "short",
                    }),
                  })}
                </Text>
              )}
            </>
          )}

          {/* Stop price input */}
          {isStop && (
            <NumberInput
              label={t("stopPriceLabel")}
              placeholder="0.00"
              value={stopPrice}
              onChange={setStopPrice}
              min={0}
              step={0.01}
              decimalScale={2}
              size="sm"
            />
          )}

          {/* Price input */}
          <NumberInput
            label={t("price")}
            placeholder={isMarket ? t("market") : "0.00"}
            value={price}
            onChange={setPrice}
            min={0}
            step={0.01}
            decimalScale={2}
            disabled={isMarket}
            size="sm"
          />

          {crossedPriceWarning && (
            <Alert color="yellow" variant="light" p="xs" icon={<TbAlertTriangle size={16} />}>
              <Text size="xs">{crossedPriceWarning}</Text>
            </Alert>
          )}

          {/* Volume input */}
          <NumberInput
            label={t("volume")}
            placeholder="0"
            value={volume}
            onChange={setVolume}
            min={0}
            step={1}
            decimalScale={0}
            size="sm"
          />

          {/* Fill type toggle */}
          <Switch
            label={t("allOrNothing")}
            checked={allOrNothing}
            onChange={(e) => setAllOrNothing(e.currentTarget.checked)}
            size="xs"
          />

          {/* Order summary sentence */}
          {qty > 0 && (
            <Text size="sm" c="dimmed">
              {side === "1"
                ? t("summaryBuy", { qty, ref: projectRefId, vintage })
                : t("summarySell", { qty, ref: projectRefId, vintage })}
              {" "}
              {isMarket
                ? subtotal > 0
                  ? t("summaryAtPrice", { price: effectivePrice.toFixed(2), total: subtotal.toFixed(2), fee: platformFee.toFixed(2) })
                  : t("summaryAtExecution")
                : subtotal > 0
                  ? t("summaryAtPrice", { price: prc.toFixed(2), total: subtotal.toFixed(2), fee: platformFee.toFixed(2) })
                  : null}
            </Text>
          )}

          {/* Fee breakdown */}
          {isMarket && subtotal === 0 ? (
            <Text size="xs" c="dimmed">{t("feeAtExecution")}</Text>
          ) : subtotal > 0 ? (
            <Stack gap={2}>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">{t("subtotal")}</Text>
                <Text size="sm">€{subtotal.toFixed(2)}</Text>
              </Group>
              <Group justify="space-between">
                <Group gap={4}>
                  <Text size="sm" c="dimmed">{t("platformFee")}</Text>
                  {feeResult && subtotal > 0 && (
                    <Text size="xs" c="dimmed">({(feeResult.rate * 100).toFixed(1)}%)</Text>
                  )}
                </Group>
                <Text size="sm">€{platformFee.toFixed(2)}</Text>
              </Group>
              <Divider my={2} />
              <Group justify="space-between">
                <Text size="sm" fw={700}>{t("totalDue")}</Text>
                <Text size="sm" fw={700}>€{totalDue.toFixed(2)}</Text>
              </Group>
            </Stack>
          ) : null}

          <Button
            fullWidth
            color={side === "1" ? "green" : "red"}
            loading={isPending}
            onClick={handleSubmit}
          >
            {t("reviewOrder")}
          </Button>

          {insufficientBalance && (
            <Alert color="orange" variant="light" p="xs" mt="xs">
              <Group justify="space-between" align="center" wrap="nowrap">
                <Text size="xs">Need €{(totalDue - availableBalance!).toFixed(2)} more to place this order.</Text>
                {onFundAccount && (
                  <Button size="xs" variant="light" color="orange" onClick={onFundAccount} style={{ flexShrink: 0 }}>
                    Fund
                  </Button>
                )}
              </Group>
            </Alert>
          )}

          {/* Off-hours info for LIMIT/STOP orders */}
          {!marketOpen && isLimitOrStop && (
            <Alert color="blue" variant="light" p="xs">
              {timeInForce === "DAY"
                ? t("dayOrderActivatesAtOpen", {
                    time: nextSessionOpen().toLocaleString(undefined, {
                      timeZone: "Europe/Oslo",
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZoneName: "short",
                    }),
                  })
                : t("orderQueuedForOpen", {
                    time: nextSessionOpen().toLocaleString(undefined, {
                      timeZone: "Europe/Oslo",
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZoneName: "short",
                    }),
                  })}
            </Alert>
          )}

          {error && (
            <Alert color="red" onClose={() => setError(null)} withCloseButton>
              {error}
            </Alert>
          )}
    </Stack>

      <TradingProfileModal
        opened={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        onSuccess={() => setProfileModalOpen(false)}
      />
      <OrderConfirmModal
        opened={confirmOpen}
        onClose={() => { setConfirmOpen(false); setPendingOrderInput(null); setBreakdown(null) }}
        orderInput={pendingOrderInput}
        breakdown={breakdown}
        onSuccess={handleOrderSuccess}
        onProfileRequired={() => { setConfirmOpen(false); setProfileModalOpen(true) }}
      />
    </>
  )
}
